<template>
  <div class="grid gap-5">
    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center py-10">
      <Loading />
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="flex flex-col items-center text-gray-500 py-10">
      <ExclamationTriangleIcon class="w-8 h-8 mb-2 text-amber-500" />
      <p class="text-sm">{{ $t('components.salesreport.server_error') }}</p>
    </div>

    <!-- No data — ghost card -->
    <ReportCard v-else-if="allClientTypeKeys.length === 0"
      :title="$t('report.client_types_chart_title')"
      title-class="text-foreground/40">
      <div class="flex items-end justify-center gap-2 h-40 px-6 mb-5">
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 35%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 60%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 25%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 80%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 45%"></div>
        <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 55%"></div>
        <div class="ct-ghost-bar flex-1 rounded-t" style="height: 30%"></div>
      </div>
      <div class="text-center pb-2">
        <p class="text-[13px] font-semibold text-muted-foreground">
          {{ $t('data.unavailable') }}
        </p>
        <p class="text-[11px] text-muted-foreground/50 mt-1">
          {{ $t('data.unavailable_hint') }}
        </p>
      </div>
    </ReportCard>

    <template v-else>
      <!-- Page-level controls -->
      <div class="flex flex-col items-center gap-3 mb-1">
        <SegmentedPicker
          v-model="aggregation"
          :groups="pickerGroups"
        />
      </div>

      <!-- Revenue chart -->
      <template v-if="clientTypeKeys.length">
        <ClientTypesRevenueChart
          ref="revenueChartRef"
          :response="response!"
          :chart-mode="chartMode"
          @update:chart-mode="chartMode = $event"
          :tax-key="taxKey"
          :center-id="centerId"
          :format-x-label="formatXLabel"
          :loading="chartLoading"
        />

        <!-- Volume chart -->
        <ClientTypesVolumeChart
          ref="volumeChartRef"
          :response="response!"
          :tax-key="taxKey"
          :format-x-label="formatXLabel"
          :loading="chartLoading"
        />
      </template>

      <!-- Ghost card when no current data but previous exists -->
      <ReportCard v-else
        :title="$t('report.client_types_chart_title')"
        title-class="text-foreground/40">
        <div class="flex items-end justify-center gap-2 h-40 px-6 mb-5">
          <div class="ct-ghost-bar flex-1 rounded-t" style="height: 35%"></div>
          <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 60%"></div>
          <div class="ct-ghost-bar flex-1 rounded-t" style="height: 25%"></div>
          <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 80%"></div>
          <div class="ct-ghost-bar flex-1 rounded-t" style="height: 45%"></div>
          <div class="ct-ghost-bar ct-ghost-bar--accent flex-1 rounded-t" style="height: 55%"></div>
          <div class="ct-ghost-bar flex-1 rounded-t" style="height: 30%"></div>
        </div>
        <div class="text-center pb-2">
          <p class="text-[13px] font-semibold text-muted-foreground">
            {{ $t('data.unavailable') }}
          </p>
        </div>
      </ReportCard>

      <!-- Average Ticket + Client Mix side by side -->
      <div class="grid grid-cols-2 gap-5">
        <ClientTypesAvgTicket
          :response="response!"
          :tax-key="taxKey"
          :center-id="centerId"
        />

        <ClientTypesMixEvolution
          :response="response!"
          :tax-key="taxKey"
          :current-period-label="currentPeriodLabel"
          :previous-period-label="previousPeriodLabel"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { useReportContext } from '../../../composables/useReportContext'
import { useTimeAggregation } from '../../../composables/useTimeAggregation'
import type { SalesByClientTypesResponse } from '../../../responses'
import ReportCard from '../../ReportCard.vue'
import Loading from '../../Loading.vue'
import SegmentedPicker, { type PickerGroup } from '../../ui/SegmentedPicker.vue'
import ClientTypesRevenueChart from '../../report-sections/ClientTypesRevenueChart.vue'
import ClientTypesVolumeChart from '../../report-sections/ClientTypesVolumeChart.vue'
import ClientTypesAvgTicket from '../../report-sections/ClientTypesAvgTicket.vue'
import ClientTypesMixEvolution from '../../report-sections/ClientTypesMixEvolution.vue'

const { t } = useI18n()
const { card, centerId } = useReportContext()

const isLoading = ref(false)
const chartLoading = ref(false)
const error = ref(false)
const response = ref<SalesByClientTypesResponse | null>(null)
const chartMode = ref<'percent' | 'absolute'>('percent')

const revenueChartRef = ref<InstanceType<typeof ClientTypesRevenueChart>>()
const volumeChartRef = ref<InstanceType<typeof ClientTypesVolumeChart>>()

const startDate = computed(() => card.value?.date?.startDate?.toJSDate() ?? new Date())
const endDate = computed(() => card.value?.date?.endDate?.toJSDate() ?? new Date())

const currentPeriodLabel = computed(() => card.value?.date?.formatBoundaries() ?? '')
const previousPeriodLabel = computed(() => card.value?.previousDate?.formatBoundaries() ?? '')
const { aggregation, aggregationOptions, formatXLabel } = useTimeAggregation(startDate, endDate)

const pickerGroups = computed<PickerGroup[]>(() => {
  const chronological = aggregationOptions.value.filter(o => o.group === 'chronological')
  const distribution = aggregationOptions.value.filter(o => o.group === 'distribution')
  const groups: PickerGroup[] = []
  if (chronological.length) {
    groups.push({
      label: t('time_aggregation.group_chronological'),
      options: chronological.map(o => ({ value: o.value, label: o.label })),
    })
  }
  if (distribution.length) {
    groups.push({
      label: t('time_aggregation.group_distribution'),
      options: distribution.map(o => ({ value: o.value, label: o.label })),
    })
  }
  return groups
})

// Types with current interval data (for Revenue & Volume charts)
const clientTypeKeys = computed<string[]>(() => {
  if (!response.value?.client_types) return []
  return Object.keys(response.value.client_types).filter(type => {
    const data = response.value!.client_types[type]
    if (!data?.intervals?.length) return false
    return data.intervals.some(i =>
      i.data.period.tax_data.excluded_tax !== 0 || i.data.period.tax_data.included_tax !== 0,
    )
  })
})

// Types with current OR previous data (for Ticket Moyen & Mix Client)
const allClientTypeKeys = computed<string[]>(() => {
  if (!response.value?.client_types) return []
  return Object.keys(response.value.client_types).filter(type => {
    const data = response.value!.client_types[type]
    if (!data?.summary) return false
    const cur = data.summary.total_period.tax_data
    const prev = data.summary.total_previous_period?.tax_data
    return (cur.excluded_tax !== 0 || cur.included_tax !== 0)
      || (prev && (prev.excluded_tax !== 0 || prev.included_tax !== 0))
  })
})

const taxKey = computed(() => {
  return card.value?.taxIncluded ? 'included_tax' : 'excluded_tax'
})

// Restore aggregation from model if already cached
const restoring = ref(false)
if (card.value?.clientTypesAggregation) {
  restoring.value = true
  aggregation.value = card.value.clientTypesAggregation as typeof aggregation.value
  // Use the cached response directly
  response.value = card.value.clientTypesReport
  setTimeout(() => { restoring.value = false }, 0)
} else if (card.value?.clientTypesReport) {
  // Data fetched by ReportDesktopCard on mount, just use it
  response.value = card.value.clientTypesReport
}

// Watch for data arriving from ReportDesktopCard background fetch
watch(() => card.value?.clientTypesReport, (newReport) => {
  if (newReport && !response.value) {
    response.value = newReport
  }
})

watch(aggregation, async (newAgg) => {
  if (!card.value || restoring.value) return
  chartLoading.value = true
  try {
    await card.value.fetchClientTypesReport(newAgg)
    response.value = card.value.clientTypesReport
  } catch {
    error.value = true
  } finally {
    chartLoading.value = false
  }
  await nextTick()
  revenueChartRef.value?.refreshChart()
  volumeChartRef.value?.refreshChart()
})

// Save aggregation when leaving so it can be restored
onBeforeUnmount(() => {
  if (!card.value) return
  card.value.clientTypesAggregation = aggregation.value
})
</script>

<style scoped>
/* Ghost bar chart — empty state */
.ct-ghost-bar {
  background: rgba(156, 163, 175, 0.10);
  animation: ct-ghost-pulse 3s ease-in-out infinite;
}
.ct-ghost-bar--accent {
  background: rgba(6, 200, 255, 0.08);
  animation-delay: 0.4s;
}
.ct-ghost-bar:nth-child(3) { animation-delay: 0.8s; }
.ct-ghost-bar:nth-child(5) { animation-delay: 1.2s; }
.ct-ghost-bar:nth-child(7) { animation-delay: 1.6s; }

@keyframes ct-ghost-pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

:is(.dark *) .ct-ghost-bar {
  background: rgba(255, 255, 255, 0.04);
}
:is(.dark *) .ct-ghost-bar--accent {
  background: rgba(6, 200, 255, 0.06);
}
</style>
